import { NgZone } from '@angular/core';

/** Bounds every portal's session timeout shares (mirrors Auth M's clamp). */
export const MIN_SESSION_TIMEOUT_MINUTES = 5;
export const MAX_SESSION_TIMEOUT_MINUTES = 240;
export const DEFAULT_SESSION_TIMEOUT_MINUTES = 30;

/** The per-user preference each Auth M session stores at `…/me/settings`. */
export interface SessionSettings {
  session_timeout_minutes: number;
}

export function clampTimeoutMinutes(minutes: number): number {
  const m = Number.isFinite(minutes) ? minutes : DEFAULT_SESSION_TIMEOUT_MINUTES;
  return Math.min(Math.max(m, MIN_SESSION_TIMEOUT_MINUTES), MAX_SESSION_TIMEOUT_MINUTES);
}

/**
 * The inactivity timer behind every portal's session timeout — admin, invited
 * and end-user. Any user activity (pointer, keyboard, scroll, touch, returning
 * to the tab) counts; once the idle period elapses it stops itself and calls
 * `onExpire`, and each session decides what ending means for it.
 *
 * Idle time is measured from the LAST ACTIVITY rather than trusted to one long
 * setTimeout. Browsers throttle timers in background tabs and suspend them
 * while the device sleeps, so a timer alone would let a laptop that slept for
 * hours wake straight back into a live session. Here, the first activity after
 * an over-long gap ends the session instead of extending it.
 */
export class IdleTimer {
  private static readonly EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];

  private timeoutMs = DEFAULT_SESSION_TIMEOUT_MINUTES * 60_000;
  private lastActivity = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;

  constructor(
    private readonly zone: NgZone,
    private readonly onExpire: () => void,
  ) {}

  get isRunning(): boolean {
    return this.running;
  }

  /** Start watching, or apply a new timeout to the current watch. */
  watch(minutes: number): void {
    if (typeof window === 'undefined') return;
    this.timeoutMs = clampTimeoutMinutes(minutes) * 60_000;
    this.lastActivity = Date.now();
    if (!this.running) {
      this.running = true;
      // Listeners run OUTSIDE Angular so mousemove doesn't trigger change
      // detection storms; only expiry re-enters the zone.
      this.zone.runOutsideAngular(() => {
        for (const e of IdleTimer.EVENTS) {
          window.addEventListener(e, this.onActivity, { capture: true, passive: true });
        }
        document.addEventListener('visibilitychange', this.onVisibility, true);
      });
    }
    this.schedule(this.timeoutMs);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    for (const e of IdleTimer.EVENTS) {
      window.removeEventListener(e, this.onActivity, { capture: true });
    }
    document.removeEventListener('visibilitychange', this.onVisibility, true);
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  // Activity only records a timestamp — no timer churn on every mousemove.
  private readonly onActivity = (): void => {
    if (!this.running) return;
    const now = Date.now();
    if (now - this.lastActivity >= this.timeoutMs) {
      // The idle period already passed while timers were throttled or the
      // device slept: end the session rather than extend it.
      this.expire();
      return;
    }
    this.lastActivity = now;
  };

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') this.onActivity();
  };

  private schedule(delayMs: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => this.check(), Math.max(delayMs, 1000));
    });
  }

  private check(): void {
    if (!this.running) return;
    const idleFor = Date.now() - this.lastActivity;
    if (idleFor >= this.timeoutMs) {
      this.expire();
    } else {
      this.schedule(this.timeoutMs - idleFor);
    }
  }

  private expire(): void {
    this.zone.run(() => {
      this.stop();
      this.onExpire();
    });
  }
}
